import { CompanyCompanyAdminResourceCompanyOutputJsonldType as CompanyType } from "@/lib/api/generated/model";

/**
 * Sortable columns of the companies list (mirrors the backend order params).
 *
 * Only `name` is used at present: the list has no user-facing sort control, but
 * the collection must still be requested in a deterministic order, and the
 * search shape carries it so a later slice can expose sorting without changing
 * the URL contract.
 */
export type CompanySortBy = "name" | "groupName" | "type";
/** Sort direction, matching the generated `order[...]` enums. */
export type CompanySortDirection = "ASC" | "DESC";
/** The `Alle / Verzekeraars / Lease` type filter of the concept's toolbar. */
export type CompanyTypeFilter = "all" | "insurance" | "lease";

/**
 * The `/companies` route's URL search shape plus its default value. Lives in a
 * plain, non-route module so other features can build a fully-typed search
 * object for `navigate({ to: "/companies", search })` without importing the
 * route module (which would couple them and can defeat code splitting).
 */
export interface CompaniesSearch {
  readonly page: number;
  /** Free-text query on name, group name and city. */
  readonly query: string;
  readonly sortBy: CompanySortBy;
  readonly sortDirection: CompanySortDirection;
  readonly type: CompanyTypeFilter;
}

export const DEFAULT_COMPANIES_SEARCH: CompaniesSearch = Object.freeze({
  page: 1,
  query: "",
  sortBy: "name",
  sortDirection: "ASC",
  type: "all",
});

const SORT_BY_VALUES: ReadonlySet<string> = new Set<CompanySortBy>([
  "name",
  "groupName",
  "type",
]);

const TYPE_FILTER_VALUES: ReadonlySet<string> = new Set<CompanyTypeFilter>([
  "all",
  "insurance",
  "lease",
]);

/**
 * The company types this directory covers. The collection endpoint is *not*
 * scoped server-side, so every request must narrow it explicitly or dealerships
 * — which are not opdrachtgevers — leak into the list.
 */
export const OPDRACHTGEVER_TYPES: readonly string[] = Object.freeze([
  CompanyType.insurance,
  CompanyType.lease,
]);

/** Coerces an unknown search param into a valid `sortBy`, defaulting to name. */
export function coerceSortBy(value: unknown): CompanySortBy {
  return typeof value === "string" && SORT_BY_VALUES.has(value)
    ? (value as CompanySortBy)
    : DEFAULT_COMPANIES_SEARCH.sortBy;
}

/** Coerces an unknown search param into a valid sort direction. */
export function coerceSortDirection(value: unknown): CompanySortDirection {
  return value === "DESC" ? "DESC" : "ASC";
}

/** Coerces an unknown search param into a valid type filter. */
export function coerceTypeFilter(value: unknown): CompanyTypeFilter {
  return typeof value === "string" && TYPE_FILTER_VALUES.has(value)
    ? (value as CompanyTypeFilter)
    : DEFAULT_COMPANIES_SEARCH.type;
}

/**
 * Builds the `type[]` request parameter. A specific filter narrows to that one
 * type; "all" still sends both opdrachtgever types rather than omitting the
 * parameter, because omitting it would include dealerships.
 */
export function toTypeParam(filter: CompanyTypeFilter): string[] {
  return filter === "all" ? [...OPDRACHTGEVER_TYPES] : [filter];
}
